import { useMemo, memo } from 'react';
import styles from './IncidentDetailsSection.module.css';
import JoinBridgeButton from './JoinBridgeButton';
import { buildSlackChannelUrl } from '../../../constants/slackConstants';
import { formatTimestamp } from '../Status/statusUtils';
import {
  shouldShowNoIncidentLinked,
  NO_COMMAND_CENTER_INCIDENT_MESSAGE,
} from '../../../utils/incidentLinkage';

/**
 * IncidentDetailsSection
 *
 * Renders the "Incident Details" card section on the Modern incident detail page.
 * All fields flow in a single wrapping row: conference bridge, Slack channel,
 * Incident Commander, Major Incident Manager, Incident ID, PRB Number, Last Updated.
 *
 * US1889423 scope.
 * US1920991 scope: label rename (Major Incident Manager), IC/MIM always visible,
 * Incident ID/PRB collapsible, de-uppercase labels.
 * US1975600 scope: Show More/Show Less toggle removed (metadata always visible);
 * Slack channel hidden when incident is resolved; all fields unified into one
 * wrapping row.
 *
 * @param {Object}      props
 * @param {string}      props.problemId               - Problem number, passed to JoinBridgeButton
 * @param {boolean}     props.showBridge               - Whether to render the conference bridge button
 * @param {string}      props.slackChannel             - Slack channel name (e.g. "#incident-foo-001")
 * @param {string}      props.incidentCommander        - Incident Commander name or email (always visible)
 * @param {string}      props.majorIncidentManager     - Major Incident Manager name or email (always visible)
 * @param {string}      props.incidentNumber           - ServiceNow INC number for deep-link
 * @param {string}      props.prbNumber                - PRB number for deep-link
 * @param {number|null} props.lastUpdated              - Unix timestamp (ms) of most recent audit change; 0 = not set
 * @param {boolean}     props.loading                  - Show skeleton placeholders while data loads
 * @param {string}      props.snowHost                 - ServiceNow base URL from config API
 * @param {Object}      props.newrelic                 - NewRelic browser agent instance (passed through from AppWrapper)
 * @param {boolean}     [props.isResolved]             - When true, the Slack Channel cell is hidden
 *                                                       (incident is in a Resolve state — read-only view)
 */
function IncidentDetailsSection({
  problemId,
  showBridge,
  slackChannel,
  incidentCommander,
  majorIncidentManager,
  incidentNumber,
  awscase,
  prbNumber,
  lastUpdated,
  loading = false,
  snowHost = 'https://cox.service-now.com',
  newrelic,
  isResolved = false,
}) {
  // Memoised URL construction — only recompute when the relevant fields change.
  const slackHref = useMemo(
    () =>
      slackChannel
        ? buildSlackChannelUrl(slackChannel)
        : null,
    [slackChannel]
  );

  // nav_to.do is the ServiceNow entry point that handles auth redirects cleanly.
  // Linking directly to incident.do/problem.do can trigger an infinite login loop
  // when ServiceNow tries to redirect through its auth flow.
  const snowIncidentHref = useMemo(
    () =>
      incidentNumber
        ? `${snowHost}/nav_to.do?uri=${encodeURIComponent(`/incident.do?sysparm_query=number=${incidentNumber}`)}`
        : null,
    [incidentNumber, snowHost]
  );

  const snowPrbHref = useMemo(
    () =>
      prbNumber && snowHost
        ? `${snowHost}/nav_to.do?uri=${encodeURIComponent(`/problem.do?sysparm_query=number=${prbNumber}`)}`
        : null,
    [prbNumber, snowHost]
  );

  // US2031585: when a real PRB has no EOC Bridge incident linked, surface an explicit
  // "No incident linked in an appropriate assignment group" state instead of a bare em-dash.
  const showNoIncidentLinked = shouldShowNoIncidentLinked(incidentNumber, prbNumber);

  // Backend stores 0 to mean "not set" — treat identically to null.
  const hasLastUpdated = lastUpdated != null && lastUpdated > 0;
  const formattedLastUpdated = hasLastUpdated
    ? formatTimestamp(lastUpdated, { relative: false, includeTime: true })
    : null;

  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading incident details">
        <div className={styles.skeletonRow} />
        <div className={styles.skeletonGrid} />
      </div>
    );
  }

  const hasMetadata = !!(incidentNumber || prbNumber || hasLastUpdated || awscase);

  // Always-visible row: Bridge, Slack Channel, IC, MIM — all side-by-side.
  // Slack is excluded from this count when resolved (cell won't render).
  const hasAlwaysVisible = showBridge || (!!slackChannel && !isResolved) || !!incidentCommander || !!majorIncidentManager;

  return (
    <div>
      {/* ── Single wrapping row: all detail cells flow left-to-right, wrap as needed ── */}
      {(hasAlwaysVisible || hasMetadata) && (
        <div className={styles.communicationsRow}>
          {showBridge && (
            <div className={styles.commCell}>
              <JoinBridgeButton problemId={problemId} newrelic={newrelic} />
            </div>
          )}

          {slackChannel && !isResolved && (
            <div className={styles.commCell}>
              <div className={styles.commCellInner}>
                <span className={styles.fieldLabel}>Slack Channel</span>
                <a
                  href={slackHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.fieldLink}
                  aria-label={`Open Slack channel ${slackChannel} (opens in new tab)`}
                >
                  {slackChannel}
                </a>
              </div>
            </div>
          )}

          {incidentCommander && (
            <div className={styles.commCell}>
              <dl className={styles.commCellInner}>
                <dt className={styles.fieldLabel}>Incident Commander</dt>
                <dd className={styles.fieldValue}>{incidentCommander}</dd>
              </dl>
            </div>
          )}

          {majorIncidentManager && (
            <div className={styles.commCell}>
              <dl className={styles.commCellInner}>
                <dt className={styles.fieldLabel}>Major Incident Manager</dt>
                <dd className={styles.fieldValue}>{majorIncidentManager}</dd>
              </dl>
            </div>
          )}

          {/* Cells render in order PRB → INC → AWS (US2031587 [A3]). The PRB cell is
              gated on prbNumber (US2031592 [B5]): INC-only records omit it entirely
              rather than showing an em-dash. Incident ID always renders; AWS Case #
              renders only when set (WCAG 1.3.1: absent values announced). */}
          {hasMetadata && (
            <>
              {prbNumber && (
                <div className={styles.commCell}>
                  <dl className={styles.commCellInner}>
                    <dt className={styles.fieldLabel}>PRB Number</dt>
                    <dd className={styles.fieldValue}>
                      {snowPrbHref ? (
                        <a
                          href={snowPrbHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.fieldLink}
                          aria-label={`Open ServiceNow problem ${prbNumber} (opens in new tab)`}
                        >
                          {prbNumber}
                        </a>
                      ) : (
                        <span>{prbNumber}</span>
                      )}
                    </dd>
                  </dl>
                </div>
              )}

              <div className={styles.commCell}>
                <dl className={styles.commCellInner}>
                  <dt className={styles.fieldLabel}>Incident ID</dt>
                  <dd className={styles.fieldValue}>
                    {snowIncidentHref ? (
                      <a
                        href={snowIncidentHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.fieldLink}
                        aria-label={`Open ServiceNow incident ${incidentNumber} (opens in new tab)`}
                      >
                        {incidentNumber}
                      </a>
                    ) : showNoIncidentLinked ? (
                      <span
                        className={styles.noIncidentLinked}
                        aria-label={NO_COMMAND_CENTER_INCIDENT_MESSAGE}
                      >
                        {NO_COMMAND_CENTER_INCIDENT_MESSAGE}
                      </span>
                    ) : (
                      <span aria-label={incidentNumber ? undefined : 'Not set'}>
                        {incidentNumber || '—'}
                      </span>
                    )}
                  </dd>
                </dl>
              </div>

              {/* US2031587 [A3]: AWS Case # cell (display-only), rendered last. */}
              {awscase && (
                <div className={styles.commCell}>
                  <dl className={styles.commCellInner}>
                    <dt className={styles.fieldLabel}>AWS Case #</dt>
                    <dd className={styles.fieldValue}>{awscase}</dd>
                  </dl>
                </div>
              )}
            </>
          )}

          {formattedLastUpdated && (
            <div className={styles.commCell}>
              <dl className={styles.commCellInner}>
                <dt className={styles.fieldLabel}>Last Updated</dt>
                <dd className={styles.fieldValue}>{formattedLastUpdated}</dd>
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(IncidentDetailsSection);
